import React from "react"
import Icon from "icons"
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom"
import classNames from "classnames"

import request from "request"
import global from "global"
import Loading from "components/loading"
import styles from "./application-group.module.scss"
import { toast } from "react-toastify"

const ApplicationGroupLayout = () => {
  const { id, clientId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [pagePending, setPagePending] = React.useState(false)
  const [group, setGroup] = React.useState<global.group_applications>()
  const [legalCaseList, setLegalCaseList] = React.useState<legalCase[]>([])
  const [guarantorLegalCaseList, setGuarantorLegalCaseList] = React.useState<
    legalCase[]
  >([])
  const [OCRFileList, setOCRFileList] = React.useState<OCRFile[]>([])
  const [pendingOCR, setPendingOCR] = React.useState(false)

  React.useEffect(() => {
    setPagePending(true)
    request
      .get(`/applications/group/${id}`)
      .then((res) => res.data)
      .then((res) => {
        setGroup(res)
        setLegalCaseList(
          res.applications.map((app: global.application) => ({
            id: app.id,
            hasLegalCase: false,
            legal_case_has_correct_type: null,
            legal_case_date: "",
          })),
        )
        setGuarantorLegalCaseList(
          res.applications.map((app: global.application) => ({
            id: app.id,
            hasLegalCase: false,
            legal_case_has_correct_type: null,
            legal_case_date: "",
          })),
        )
        setOCRFileList(
          res.applications.map((app: global.application) => ({
            id: app.id,
          })),
        )
      })
      .catch((error) => {
        toast.error(
          `Error fetching application group: ${error.response?.data?.message || error.message}`,
        )
      })
      .finally(() => setPagePending(false))
  }, [id])

  if (pagePending || !group)
    return (
      <div className={styles.page_loading}>
        <Loading />
      </div>
    )
  return (
    <div className={styles.applicationGroupLayout}>
      <div className={styles.navbar_wrap}>
        <div className={styles.navbar}>
          <NavLink
            end
            type="button"
            draggable={false}
            className={styles.link_wrap}
            to={`/application-group/${id}`}
          >
            {({ isActive }) => (
              <div
                className={classNames(styles.link, isActive && styles.active)}
              >
                <Icon
                  className={styles.icon}
                  name={isActive ? "homeFilled" : "home"}
                />
                Group info
              </div>
            )}
          </NavLink>
        </div>
        <div className={styles.navbar}>
          {group?.applications
            .sort((a, b) => a.id - b.id)
            .map((application, index) => (
              <NavLink
                key={application.id}
                type="button"
                draggable={false}
                to={`/application-group/${id}/client/${application.id}`}
              >
                {({ isActive }) => {
                  const lastPath =
                    location.pathname.split("/")[
                      location.pathname.split("/").length - 1
                    ]
                  return (
                    <button
                      type="button"
                      className={styles.link_wrap}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        navigate(
                          `/application-group/${id}/client/${application.id}/scoring`,
                        )
                      }}
                    >
                      <div
                        className={classNames(
                          styles.link,
                          isActive && styles.active,
                        )}
                      >
                        <Icon
                          className={styles.icon}
                          name={isActive ? "personFilled" : "person"}
                        />
                        Client {index + 1}
                      </div>
                    </button>
                  )
                }}
              </NavLink>
            ))}
        </div>
        <div className={styles.navbar}>
          {group.scoring_approved !== null && (
            <NavLink
              type="button"
              draggable={false}
              className={styles.link_wrap}
              to={`/application-group/${id}/result`}
            >
              {({ isActive }) => (
                <div
                  className={classNames(styles.link, isActive && styles.active)}
                >
                  <Icon
                    className={styles.icon}
                    name={isActive ? "verifyFill" : "verify"}
                  />
                  Result
                </div>
              )}
            </NavLink>
          )}
          <button
            type="button"
            className={styles.link_wrap}
            onClick={() => navigate("/group-loans")}
          >
            <div className={styles.link}>
              <Icon className={styles.icon} name="longArrowLeft" />
              Back
            </div>
          </button>
        </div>
      </div>
      <Outlet
        context={{
          application: group?.applications.find(
            (application) => application.id === Number(clientId),
          ),
          pendingOCR,
          setPendingOCR,
          group,
          setGroup,
          legalCaseList,
          guarantorLegalCaseList,
          legalCase: legalCaseList.find(
            (legalCase) => legalCase.id === Number(clientId),
          ),
          setLegalCase: (newL: legalCase) =>
            setLegalCaseList(
              legalCaseList.map((l) => (l.id === newL.id ? newL : l)),
            ),
          guarantorLegalCase: guarantorLegalCaseList.find(
            (legalCase) => legalCase.id === Number(clientId),
          ),
          setGuarantorLegalCase: (newL: legalCase) =>
            setGuarantorLegalCaseList(
              guarantorLegalCaseList.map((l) => (l.id === newL.id ? newL : l)),
            ),
          OCRFileList,
          OCRFile: OCRFileList.find(
            (OCRFile) => OCRFile.id === Number(clientId),
          ),
          setOCRFile: (newO: OCRFile) =>
            setOCRFileList(
              OCRFileList.map((l) => (l.id === newO.id ? newO : l)),
            ),
        }}
      />
    </div>
  )
}

type OCRFile = {
  id: number
  file?: File
}

type legalCase = {
  id: number
  hasLegalCase: boolean
  legal_case_has_correct_type: "Yes" | "No" | null
  legal_case_date: string
}

export interface ClientContextType {
  application: global.application
  pendingOCR: boolean
  setPendingOCR: React.Dispatch<React.SetStateAction<boolean>>
  group: global.group_applications
  setGroup: (value: global.group_applications) => void
  OCRFileList: OCRFile[]
  OCRFile: OCRFile
  setOCRFile: (OCRFile: OCRFile) => void
  legalCaseList: legalCase[]
  guarantorLegalCaseList: legalCase[]
  legalCase: legalCase
  setLegalCase: (legalCase: legalCase) => void
  guarantorLegalCase: legalCase
  setGuarantorLegalCase: (legalCase: legalCase) => void
}

export const useClientContext = () => {
  return useOutletContext<ClientContextType>()
}

export default ApplicationGroupLayout
